import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { putWpsHold } from '../../../../api/Payroll/wps';
import { WpsHoldReason } from '../../../../models/payroll';
import { InlineConfirm } from './InlineConfirm';
import { useWpsConfirm } from './useWpsConfirm';
import { useWpsLabels, wpsHoldClosesMonth } from './wpsLabels';

const HOLD_REASONS: WpsHoldReason[] = [
  'PaidAnotherWay',
  'BankDetailsPending',
  'DocumentedUnpaidLeave',
  'WorkAbandonmentReported',
  'CourtWageDispute',
  'Other',
];

interface HoldBackFormProps {
  year: number;
  month: number;
  userId: string;
  employeeName: string;
  initialReason?: WpsHoldReason | null;
  initialNote?: string | null;
  onDone: () => void;
  onCancel: () => void;
}

/** Opens in the row. Says what the reason does to the month before HR commits, and warns if the row is marked paid. */
export const HoldBackForm: React.FC<HoldBackFormProps> = ({
  year,
  month,
  userId,
  employeeName,
  initialReason,
  initialNote,
  onDone,
  onCancel,
}) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [reason, setReason] = useState<WpsHoldReason | ''>(initialReason ?? '');
  const [note, setNote] = useState(initialNote ?? '');
  const finished = useCallback(() => onDone(), [onDone]);
  const { warnings, error, busy, run } = useWpsConfirm(finished);

  const noteNeeded = reason === 'Other';
  const canSubmit = reason !== '' && (!noteNeeded || note.trim().length > 0);

  const submit = () => {
    if (reason === '') return;
    const trimmed = note.trim();
    void run(() => putWpsHold(year, month, userId, {
      reason,
      // A note typed for Other and then hidden by another choice is not sent.
      note: noteNeeded ? trimmed : undefined,
      // The warning has been on screen once it is set, so the second press is the confirmation.
      confirm: warnings !== null,
    }));
  };

  return (
    <InlineConfirm
      warnings={warnings}
      error={error}
      busy={busy}
      confirmLabel={warnings
        ? t('payroll.wps.uae.hold.confirmAnyway')
        : t('payroll.wps.uae.hold.confirm', { name: employeeName })}
      canConfirm={canSubmit}
      onConfirm={submit}
      onCancel={onCancel}
      testId="wps-hold-form"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('payroll.wps.uae.hold.reason')}
          placeholder={t('payroll.wps.uae.hold.reasonPlaceholder')}
          value={reason}
          options={HOLD_REASONS.map(value => ({ value, label: labels.holdReason(value) }))}
          onChange={(value: WpsHoldReason) => setReason(value)}
          data-testid="wps-hold-reason"
        />
        {noteNeeded && (
          <Input
            label={t('payroll.wps.uae.note')}
            value={note}
            maxLength={500}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
            data-testid="wps-hold-note"
          />
        )}
      </div>
      {reason !== '' && (
        <p className="text-sm text-muted-foreground" data-testid="wps-hold-effect">
          {wpsHoldClosesMonth(reason)
            ? t('payroll.wps.uae.hold.closesMonth', { name: employeeName })
            : t('payroll.wps.uae.hold.keepsOpen', { name: employeeName })}
          {reason === 'PaidAnotherWay' && <> {t('payroll.wps.uae.hold.notExemption')}</>}
        </p>
      )}
    </InlineConfirm>
  );
};
